import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Copy, ExternalLink, LogOut, Wallet } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { getSolanaExplorerUrl } from '../../utils/solanaUtils';
import { StatusDot } from '../portal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const truncateAddress = (address: string) =>
  `${address.slice(0, 4)}...${address.slice(-4)}`;

const MENU_ITEM_CLASSES =
  'flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-sm text-seeker-400 transition-colors duration-150 focus:bg-white/5 focus:text-white hover:text-white';

/** Figma "Component 1020": white Connect pill / dark connected address pill. */
export function WalletPill() {
  const { publicKey, disconnect, connected, wallet } = useWallet();
  const { setVisible } = useWalletModal();
  const previousConnected = useRef(connected);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip toast on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousConnected.current = connected;
      return;
    }

    if (connected && !previousConnected.current && wallet) {
      toast.success(`${wallet.adapter.name} wallet connected`);
    } else if (!connected && previousConnected.current) {
      toast.success('Wallet disconnected');
    }

    previousConnected.current = connected;
  }, [connected, wallet]);

  const copyAddress = () => {
    if (publicKey) {
      navigator.clipboard.writeText(publicKey.toBase58());
      toast.success('Wallet address copied!');
    }
  };

  const openExplorer = () => {
    if (publicKey) {
      window.open(
        getSolanaExplorerUrl('address', publicKey.toBase58()),
        '_blank',
      );
    }
  };

  if (!connected) {
    return (
      <button
        type="button"
        onClick={() => setVisible(true)}
        className="flex h-9 w-full items-center justify-center gap-2 rounded-xl bg-white text-black transition-[background-color,transform] duration-150 ease-out hover:bg-seeker-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-950"
      >
        <Wallet className="size-4" />
        <span className="text-sm font-bold leading-5">Connect</span>
      </button>
    );
  }

  return (
    <DropdownMenu>
      <div className="flex h-9 w-full items-center gap-2 rounded-xl bg-seeker-900 pl-4 pr-2 text-white">
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-full min-w-0 flex-1 items-center gap-2 rounded-md text-left transition-colors duration-150 hover:text-seeker-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70"
          >
            <StatusDot className="text-green-400" />
            <span
              title={publicKey?.toBase58()}
              className="min-w-0 flex-1 truncate text-sm leading-5 tabular-nums"
            >
              {publicKey ? truncateAddress(publicKey.toBase58()) : 'Connected'}
            </span>
          </button>
        </DropdownMenuTrigger>
        <button
          type="button"
          aria-label="Disconnect wallet"
          onClick={() => disconnect()}
          className="flex size-6 shrink-0 items-center justify-center rounded-md text-seeker-400 transition-colors duration-150 hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-900"
        >
          <LogOut className="size-4" />
        </button>
      </div>

      <DropdownMenuContent
        className="w-56 rounded-lg border border-seeker-700 bg-seeker-800 p-1 text-white"
        align="start"
        sideOffset={4}
      >
        <DropdownMenuItem onClick={copyAddress} className={MENU_ITEM_CLASSES}>
          <Copy className="size-4" />
          <span>Copy Address</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openExplorer} className={MENU_ITEM_CLASSES}>
          <ExternalLink className="size-4" />
          <span>View in Explorer</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => disconnect()}
          className={`${MENU_ITEM_CLASSES} text-rose-400 focus:text-rose-300 hover:text-rose-300`}
        >
          <LogOut className="size-4" />
          <span>Disconnect</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
